import { DefaultServerService, type Server } from '@affine/core/modules/cloud';
import type { AuthSessionStatus } from '@affine/core/modules/cloud/entities/session';
import { FrameworkScope, useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';

import { AddSelfhostedStep } from './add-selfhosted';
import { SignInStep } from './sign-in';
import { SignInWithEmailStep } from './sign-in-with-email';
import { SignInWithPasswordStep } from './sign-in-with-password';

export type SignInStep =
  | 'signIn'
  | 'signInWithPassword'
  | 'signInWithEmail'
  | 'addSelfhosted';

export interface SignInState {
  step: SignInStep;
  server?: Server;
  initialServerBaseUrl?: string;
  email?: string;
  hasPassword?: boolean;
  redirectUrl?: string;
}

// tuninho: o login único OBRIGATÓRIO (card 1893242319616869943, fase 3). O servidor diz se é
// (`GET /api/tuninho/login-unico`, `plugins/tuninho/login-unico.ts`); obrigatório e sem `error`
// na URL, a tela de entrada (página e diálogo, computador e celular) segue para o login único
// em vez de mostrar o formulário; com `error` (o callback do OAuth devolveu um motivo), mostra o
// motivo e um link, sem ir sozinha — ir sozinha seria o laço que esconde o motivo. Resposta que
// falha conta como opcional: o painel de sempre.
type TuninhoLoginUnico = 'perguntando' | 'opcional' | 'obrigatorio';

const tuninhoUrlDeEntrar = () =>
  '/api/tuninho/entrar?redirect_uri=' +
  encodeURIComponent(window.location.pathname + window.location.search);

const useTuninhoLoginUnico = (): TuninhoLoginUnico => {
  const [modo, setModo] = useState<TuninhoLoginUnico>(
    BUILD_CONFIG.isNative ? 'opcional' : 'perguntando'
  );
  useEffect(() => {
    if (BUILD_CONFIG.isNative) return;
    let vivo = true;
    fetch('/api/tuninho/login-unico', { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then((d: { obrigatorio?: boolean } | null) => {
        if (vivo) setModo(d?.obrigatorio === true ? 'obrigatorio' : 'opcional');
      })
      .catch(() => {
        if (vivo) setModo('opcional');
      });
    return () => {
      vivo = false;
    };
  }, []);
  return modo;
};

export const SignInPanel = ({
  onSkip,
  server: initialServerBaseUrl,
  initStep,
  onAuthenticated,
}: {
  onAuthenticated?: (status: AuthSessionStatus) => void;
  onSkip: () => void;
  server?: string;
  initStep?: SignInStep | undefined;
}) => {
  const [state, setState] = useState<SignInState>({
    step: initStep
      ? initStep
      : initialServerBaseUrl
        ? 'addSelfhosted'
        : 'signIn',
    initialServerBaseUrl: initialServerBaseUrl,
  });

  const defaultServerService = useService(DefaultServerService);

  const step = state.step;
  const server = state.server ?? defaultServerService.server;

  const tuninhoModo = useTuninhoLoginUnico();
  const tuninhoErro = new URLSearchParams(window.location.search).get('error');
  useEffect(() => {
    if (tuninhoModo === 'obrigatorio' && !tuninhoErro) {
      window.location.href = tuninhoUrlDeEntrar();
    }
  }, [tuninhoModo, tuninhoErro]);

  if (tuninhoModo === 'perguntando') {
    return null;
  }
  if (tuninhoModo === 'obrigatorio') {
    return (
      <div style={{ padding: '24px 0', lineHeight: 1.5 }}>
        {tuninhoErro ? (
          <>
            <p style={{ marginBottom: 12, overflowWrap: 'anywhere' }}>
              {tuninhoErro}
            </p>
            <a href={tuninhoUrlDeEntrar()}>Ir para o login único</a>
          </>
        ) : (
          <p>Indo para o login único…</p>
        )}
      </div>
    );
  }

  return (
    <FrameworkScope scope={server.scope}>
      {step === 'signIn' ? (
        <SignInStep
          state={state}
          changeState={setState}
          onSkip={onSkip}
          onAuthenticated={onAuthenticated}
        />
      ) : step === 'signInWithEmail' ? (
        <SignInWithEmailStep
          state={state}
          changeState={setState}
          onAuthenticated={onAuthenticated}
        />
      ) : step === 'signInWithPassword' ? (
        <SignInWithPasswordStep
          state={state}
          changeState={setState}
          onAuthenticated={onAuthenticated}
        />
      ) : step === 'addSelfhosted' ? (
        <AddSelfhostedStep state={state} changeState={setState} />
      ) : null}
    </FrameworkScope>
  );
};
